/**
 * Authentication service and context provider
 * Manages user authentication state and API calls
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import api, { getErrorMessage } from './api'
import type {
  User,
  LoginRequest,
  LoginResponse,
  MFAVerifyRequest,
  MFAEnrollRequest,
  MFAEnrollResponse,
  SessionCheckResponse,
  TokenResponse,
} from '../types'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  login: (credentials: LoginRequest) => Promise<LoginResponse>
  verifyMFA: (data: MFAVerifyRequest) => Promise<void>
  enrollMFA: (data: MFAEnrollRequest) => Promise<MFAEnrollResponse>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Check authentication status on mount
  useEffect(() => {
    checkAuth()

    // Listen for unauthorized events from API interceptor
    const handleUnauthorized = () => {
      setUser(null)
    }

    // Listen for password-change-required events and redirect to profile.
    // Guard against a reload loop: while force_password_change is set, most
    // endpoints (e.g. the profile panels) return 403 password_change_required,
    // and assigning location.href to the URL we're already on reloads the page,
    // which re-fires the 403 → infinite reload. Only navigate if not already there.
    const handlePasswordChangeRequired = () => {
      const target = '/profile?action=change-password'
      if (window.location.pathname + window.location.search === target) return
      window.location.href = target
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)
    window.addEventListener('auth:password-change-required', handlePasswordChangeRequired)
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized)
      window.removeEventListener('auth:password-change-required', handlePasswordChangeRequired)
    }
  }, [])

  /**
   * Check if user is authenticated by fetching current session,
   * then fetch full profile to get roles and phone.
   */
  async function checkAuth() {
    try {
      // First verify the session is valid
      await api.get<SessionCheckResponse>('/v1/auth/session')
      // Then fetch the full user profile (includes roles, phone, locked_until)
      const profileResp = await api.get<User>('/v1/users/me')
      setUser(profileResp.data)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Login with email and password.
   *
   * Normally returns LoginResponse without setting the user — the MFA step (or
   * first-login MFA setup) still has to run. If the account has an admin-set MFA
   * exemption, the server completes the login here and returns an access_token;
   * in that case we finish exactly like verifyMFA does (header fallback for the
   * Vite proxy + load the profile) so the app is authenticated on return.
   */
  async function login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/v1/auth/login', credentials)
      if (response.data.access_token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`
        await refreshUser()
      }
      return response.data
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  }

  /**
   * Verify MFA code and complete login.
   * Cookie is set server-side; token is also stored in memory as Authorization
   * header fallback for dev environments where the Vite proxy doesn't forward
   * Set-Cookie headers reliably.
   */
  async function verifyMFA(data: MFAVerifyRequest): Promise<void> {
    try {
      const resp = await api.post<TokenResponse>('/v1/auth/mfa/verify', data)
      // Store token as Authorization header so subsequent requests work even
      // if the httpOnly cookie isn't forwarded through the Vite proxy.
      if (resp.data.access_token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${resp.data.access_token}`
      }
      await refreshUser()
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  }

  /**
   * Enroll a new MFA device
   */
  async function enrollMFA(data: MFAEnrollRequest): Promise<MFAEnrollResponse> {
    try {
      const response = await api.post<MFAEnrollResponse>('/v1/auth/mfa/enroll', data)
      return response.data
    } catch (error) {
      throw new Error(getErrorMessage(error))
    }
  }

  /**
   * Logout and clear session
   */
  async function logout(): Promise<void> {
    try {
      await api.post('/v1/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      delete api.defaults.headers.common['Authorization']
      setUser(null)
    }
  }

  /**
   * Refresh current user data from profile endpoint
   */
  async function refreshUser(): Promise<void> {
    try {
      const response = await api.get<User>('/v1/users/me')
      setUser(response.data)
    } catch (error) {
      setUser(null)
      throw new Error(getErrorMessage(error))
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    verifyMFA,
    enrollMFA,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to access auth context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
